/**
 * Cash drawer opener with progressive enhancement.
 *
 * POS hardware reality: cash drawers don't connect to PCs directly. They
 * plug into a receipt printer's RJ-11 "kick port", and the printer fires
 * a 24V pulse when it receives the ESC/POS open-drawer escape sequence
 * (ESC p 0 25 250  ==  bytes [0x1B, 0x70, 0x00, 0x19, 0xFA]).
 *
 * In a browser we have three escalating strategies:
 *
 *   1) WEB SERIAL (best — direct port → printer)
 *      Chrome / Edge on HTTPS expose `navigator.serial`. The cashier
 *      grants access once per device; thereafter we can write the
 *      open-drawer bytes directly. Requires a `user gesture` (button
 *      click), so we hook it into the explicit "Open Drawer" button +
 *      the checkout flow. Used by virtually every modern POS browser
 *      app (Square, Lightspeed Retail, Shopify POS web).
 *
 *   2) AUTO-PRINT TRIGGER (good — when a receipt prints anyway)
 *      We're already printing a receipt via `window.print` after a cash
 *      sale. If the operating-system driver of the receipt printer is
 *      configured to auto-open the drawer on every print job (the
 *      default for most thermal printer drivers in Qatar / GCC), the
 *      drawer pops the moment the receipt prints. So when a cash sale
 *      ends we trigger printPosReceipt(...) automatically — this opens
 *      the drawer as a side-effect even without WebSerial support.
 *
 *   3) AUDIT-ONLY FALLBACK
 *      If neither path is available (Safari, no printer, dev laptop),
 *      we still POST a `cash_drawer.opened` audit event so the
 *      operator can see in the report when the drawer was *supposed*
 *      to open. UX-wise this lets the cashier press the physical key
 *      on the drawer without losing the audit trail.
 *
 * The pulse strength (25/250 ms) matches the manufacturer-recommended
 * defaults for Epson, Star, Bixolon, and the no-name clones common in
 * Doha hardware stores.
 */

import { toast } from "sonner";
import { api } from "@/lib/api";

// ESC/POS bytes — DLE + DC4 1 + ESC + p + 0 + 25 + 250.
// We send both forms because some clone printers only recognise one.
const DRAWER_BYTES = new Uint8Array([
  0x1B, 0x70, 0x00, 0x19, 0xFA,  // ESC p 0 25 250
]);


// Persist the granted SerialPort in IndexedDB so we don't ask the cashier
// to pick it on every checkout. Web Serial port objects are not directly
// serialisable; we keep the *last* granted port reference in module state
// and re-acquire from getPorts() on subsequent calls.
let _cachedPort = null;

async function _getOrRequestPort(allowPrompt = false) {
  if (!("serial" in navigator)) return null;
  if (_cachedPort) return _cachedPort;
  try {
    const ports = await navigator.serial.getPorts();
    if (ports.length > 0) {
      _cachedPort = ports[0];
      return _cachedPort;
    }
  } catch { /* permissions API not ready */ }
  if (allowPrompt) {
    try {
      _cachedPort = await navigator.serial.requestPort({});
      return _cachedPort;
    } catch {
      return null;  // cashier cancelled the picker
    }
  }
  return null;
}

async function _writeBytes(port) {
  if (!port.readable) await port.open({ baudRate: 9600 });
  const writer = port.writable.getWriter();
  try {
    await writer.write(DRAWER_BYTES);
  } finally {
    writer.releaseLock();
  }
}

/**
 * Audit-only fallback. Best-effort POST; we never block the cashier on it.
 */
async function _logDrawerEvent(method, success, reason) {
  try {
    await api.post("/cash-drawer/events", {
      method, success, reason: reason || null,
      timestamp: new Date().toISOString(),
    });
  } catch {
    /* endpoint may not exist yet — ignore. The browser console retains
       the call attempt for debugging without disrupting the cashier. */
  }
}

/**
 * Public entry point. Returns:
 *   { ok: true, via: "serial" | "auto-print" | "audit-only" }
 *   { ok: false, reason: string }
 */
export async function openCashDrawer({ source = "manual", allowPrompt = false, lang = "ar" } = {}) {
  const isAr = lang === "ar";

  // Strategy 1 — WebSerial direct
  try {
    const port = await _getOrRequestPort(allowPrompt);
    if (port) {
      await _writeBytes(port);
      _logDrawerEvent("serial", true, source);
      toast.success(isAr ? "🗄️ تم فتح الصندوق" : "🗄️ Drawer opened");
      return { ok: true, via: "serial" };
    }
  } catch (e) {
    // Fall through to the next strategy. Surface only in dev console;
    // a serial write failure on production cashier machines is almost
    // always the printer being busy — the auto-print path will recover.
    // eslint-disable-next-line no-console
    console.warn("[cash-drawer] serial failed:", e.message);
  }

  // Strategy 2 — emit a print job carrying the ESC/POS sequence. The
  // OS-level printer driver handles forwarding the bytes to the
  // kick-port-attached drawer. We rely on the caller having a printer
  // configured; if not, the print dialog is silently dismissed (most
  // browsers) and we fall through to audit-only.
  try {
    if (window?.matchMedia && typeof window.print === "function") {
      _printDrawerPulse();
      _logDrawerEvent("auto-print", true, source);
      toast.success(isAr ? "🗄️ تم إرسال أمر الفتح للطابعة" : "🗄️ Drawer pulse sent");
      return { ok: true, via: "auto-print" };
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[cash-drawer] auto-print failed:", e.message);
  }

  // Strategy 3 — audit only.
  _logDrawerEvent("audit-only", false, source);
  toast(
    isAr ? "💡 افتح الصندوق يدوياً (تم تسجيل الحدث)"
         : "💡 Open drawer manually (event logged)",
    { duration: 4000 }
  );
  return { ok: false, reason: "no-driver" };
}

/**
 * Allow the cashier to (re-)bind a different serial port. Surfaces the
 * browser picker UI; only callable from a user gesture. Optional helper
 * — the "Open Drawer" button uses it on first click.
 */
export async function pairCashDrawer({ lang = "ar" } = {}) {
  const isAr = lang === "ar";
  if (!("serial" in navigator)) {
    toast.error(isAr
      ? "متصفحك لا يدعم WebSerial. استخدم Chrome/Edge."
      : "Your browser does not support WebSerial. Use Chrome/Edge.");
    return null;
  }
  try {
    _cachedPort = await navigator.serial.requestPort({});
    toast.success(isAr ? "تم إقران الصندوق" : "Drawer paired");
    return _cachedPort;
  } catch {
    return null;
  }
}

/**
 * Print-job fallback. Creates a hidden iframe carrying the ESC/POS
 * escape bytes wrapped in `text/plain` so the receipt printer driver
 * passes them through verbatim. Most thermal drivers run "raw" mode by
 * default — this is the path the auto-print receipt also uses.
 */
function _printDrawerPulse() {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "-9999px";
  iframe.style.width = "1px";
  iframe.style.height = "1px";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  // The <pre> wraps the bytes as Latin-1; many drivers treat the body
  // as raw and pass the escape sequence through to the printer port.
  const escAsString = String.fromCharCode(...DRAWER_BYTES);
  doc.write(`<!doctype html><html><head><style>@page{size:auto;margin:0}body{margin:0}</style></head><body><pre>${escAsString}</pre></body></html>`);
  doc.close();
  setTimeout(() => {
    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
    finally { setTimeout(() => iframe.remove(), 1500); }
  }, 50);
}

/**
 * Used by checkout(). Auto-opens drawer ONLY for cash-type payments,
 * because card/transfer flows shouldn't pop the till.
 */
export async function autoOpenForCashSale({ payment_type, payment_splits, lang = "ar" } = {}) {
  const hasCash = payment_splits?.some((s) => s.payment_method === "cash") || payment_type === "cash";
  if (!hasCash) return { ok: false, reason: "not-cash" };
  return openCashDrawer({ source: "auto-checkout", allowPrompt: false, lang });
}
